/**
 * 文件说明:
 *
 * hz 多主体 hooks
 *
 * ----------------------------------------
 * 创建用户: leo
 */

import {useRefData, useShallowEqualSelector, useSafeState} from 'hooks'
import {useEffect, useMemo} from 'react'
import {isPromise} from 'utils'
import {getCompanyNameMap, getMultiAgentInfoByCorpId} from 'common/multi-agent-constants'

export function useMultiAgentInfo() {
    const {corpId} = useShallowEqualSelector(({auth}) => ({
        corpId: auth.initData?.company?.corp_id,
    }))

    const data = useMemo(() => {
        const info = getMultiAgentInfoByCorpId(corpId)

        if (info) {
            return {...info, corpId}
        }
    }, [corpId])

    return data
}

export function useCompanyType() {
    return useMultiAgentInfo()?.constant
}

function testDefault(obj) {
    return obj.default || obj
}

export function useLoadMultiAgentComponent({
    setComponent,
    defaultComponent,
    getErrorStatus = false,
}) {
    const companyType = useCompanyType()

    const [reData, setReData] = useSafeState({
        status: 'init',
        LoadComponent: null,
        error: null,
    })

    const getRefData = useRefData({
        setComponent,
        defaultComponent,
        companyType,
        getErrorStatus,
        setReData,
    })

    useEffect(() => {
        async function init() {
            const refData = getRefData()

            try {
                const componentMap = refData.setComponent(getCompanyNameMap())
                const rawComponent = componentMap?.[refData.companyType] ?? refData.defaultComponent

                if (typeof rawComponent === 'undefined') {
                    refData.setReData((prevData) => ({
                        ...prevData,
                        status: 'notFound',
                    }))

                    return
                }

                if (isPromise(rawComponent)) {
                    refData.setReData((prevData) => ({
                        ...prevData,
                        status: 'loading',
                    }))

                    const Component = await rawComponent

                    refData.setReData((prevData) => ({
                        ...prevData,
                        status: 'success',
                        LoadComponent: testDefault(Component),
                    }))
                } else {
                    refData.setReData((prevData) => ({
                        ...prevData,
                        status: 'success',
                        LoadComponent: rawComponent,
                    }))
                }
            } catch (e) {
                if (refData.getErrorStatus) {
                    refData.setReData((prevData) => ({
                        ...prevData,
                        status: 'error',
                        error: e,
                    }))
                } else {
                    throw e
                }
            }
        }

        init()
    }, [])

    return {...reData, multiAgentName: companyType}
}
